import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DownloadsPage.css';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2'; // Import SweetAlert2
import moment from 'moment'; // Import moment for date formatting

const documentTypes = [
  { id: 'threeCertificates', name: '증명서', limit: 5 }, // Renamed and reordered
  { id: 'commencement', name: '착수신고서 폼', limit: 1 }, // Added limit
  { id: 'finalReport', name: '최종보고서 폼', limit: 3 }, // Added limit
  { id: 'supervisionDaily', name: '감리일보관련', limit: 3 }, // Renamed, added limit
  { id: 'supervisionDailyText', name: '기타', limit: 10 }, // Renamed, added limit
];

// Max file size per individual file in bytes (from backend config)
const MAX_SINGLE_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100MB

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

function DownloadsPage() {
  const { user } = useAuth();

  const [files, setFiles] = useState({}); // { documentType: [fileMetadata, ...]}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create a single ref to hold an object of refs
  const fileInputRefs = useRef({});

  const fetchFiles = useCallback(async (docType) => {
    try {
      const response = await axiosInstance.get(`/files/${docType}`);
      setFiles(prevFiles => ({
        ...prevFiles,
        [docType]: response.data
      }));
    } catch (err) {
      console.error(`Error fetching files for ${docType}:`, err);
      setError('파일을 불러오는 데 실패했습니다.');
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: '파일 목록을 불러오는 데 실패했습니다.',
      });
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all(documentTypes.map(doc => fetchFiles(doc.id)))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [fetchFiles]);

  const handleFileUpload = useCallback(async (documentId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadedBy", user ? user.name : "Unknown");

    try {
      await axiosInstance.post(`/files/upload/${documentId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      Swal.fire({
        icon: 'success',
        title: '업로드 성공',
        text: '파일이 성공적으로 업로드되었습니다.',
      });
      fetchFiles(documentId);
    } catch (err) {
      console.error(`Error uploading file for ${documentId}:`, err);
      let errorMessage = '파일 업로드에 실패했습니다.';
      if (err.response && err.response.status === 409) {
        errorMessage = err.response.data.message || '파일 업로드 제한에 도달했습니다.';
      } else if (err.response && err.response.status === 403) {
        errorMessage = '파일 업로드 권한이 없습니다. 다시 로그인해주세요.';
        // Optionally, force logout if 403 on upload means session is truly invalid
        // window.location.href = '/login';
      }
      Swal.fire({
        icon: 'error',
        title: '업로드 실패',
        text: errorMessage,
      });
    } finally {
      // Reset the file input value after upload attempt
      if (fileInputRefs.current[documentId]) {
        fileInputRefs.current[documentId].value = null;
      }
    }
  }, [fetchFiles, user]);

  const handleFileDownload = useCallback(async (fileId, fileName) => {
    try {
      const response = await axiosInstance.get(`/files/download/${fileId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Error downloading file ${fileId}:`, err);
      Swal.fire({
        icon: 'error',
        title: '다운로드 실패',
        text: '파일 다운로드에 실패했습니다.',
      });
    }
  }, []);

  const handleFileDelete = useCallback(async (fileId, documentId) => {
    const result = await Swal.fire({
      title: '정말로 이 파일을 삭제하시겠습니까?',
      text: "삭제된 파일은 복구할 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/files/${fileId}`);
        Swal.fire(
          '삭제됨!',
          '파일이 성공적으로 삭제되었습니다.',
          'success'
        );
        fetchFiles(documentId);
      } catch (err) {
        console.error(`Error deleting file ${fileId}:`, err);
        Swal.fire({
          icon: 'error',
          title: '삭제 실패',
          text: '파일 삭제에 실패했습니다.',
        });
      }
    }
  }, [fetchFiles]);

  if (loading) {
    return <div className="downloads-page">로딩 중...</div>;
  }

  if (error) {
    return <div className="downloads-page">오류: {error}</div>;
  }

  const isAdmin = user && user.role === 'ADMIN';
  const isMidAdmin = user && user.role === 'MIDADMIN';

  return (
    <div className="downloads-page">
      <br/><br/>
      <span className="limit-description">* 업로드 및 삭제는 관리자 권한이 필요하며, 다운로드는 중간관리자 까지 가능합니다.</span>
      <br/><br/><hr/><br/>
      <div className="document-sections">
        {documentTypes.map((doc) => {
          const currentFiles = files[doc.id] || [];
          const usedCapacityBytes = currentFiles.reduce((sum, file) => sum + file.fileSize, 0);
          const totalAllowedCapacityBytes = doc.limit * MAX_SINGLE_FILE_SIZE_BYTES;

          return (
            <div key={doc.id} className="document-section">
              <div className="document-title-upload">
                <h2>{doc.name}</h2>
                <span className="limit-description">
                  * 파일당 최대 {formatFileSize(MAX_SINGLE_FILE_SIZE_BYTES)}, 총 {doc.limit}개 파일 업로드 가능
                </span>
                <div className="capacity-info">
                  <span>{formatFileSize(usedCapacityBytes)} / {formatFileSize(totalAllowedCapacityBytes)}</span>
                </div>
                {isAdmin && ( // Show upload icon only for ADMIN
                  <>
                    <input
                      type="file"
                      id={`upload-${doc.id}`}
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(doc.id, e)}
                      ref={(el) => (fileInputRefs.current[doc.id] = el)}
                    />
                    <label htmlFor={`upload-${doc.id}`} className="upload-icon-label">
                      <i className="fas fa-upload upload-icon"></i>
                    </label>
                  </>
                )}
              </div>
              <div className="file-list">
                {currentFiles.length > 0 ? (
                  <ul>
                    {currentFiles.map(file => (
                      <li key={file.id} className="file-item">
                        <div className="file-info">
                          <span>{file.fileName}</span>
                          <span className="file-details">({formatFileSize(file.fileSize)} / {moment(file.uploadDate).format('YYYY-MM-DD HH:mm')})</span>
                        </div>
                        <div className="file-item-actions">
                          {(isAdmin || isMidAdmin) && ( // Show download button for ADMIN or MIDADMIN
                            <button onClick={() => handleFileDownload(file.id, file.fileName)} className="download-button small-button">
                              <i className="fas fa-download"></i> 다운로드
                            </button>
                          )}
                          {isAdmin && ( // Show delete button only for ADMIN
                            <button onClick={() => handleFileDelete(file.id, doc.id)} className="delete-button small-button">
                              <i className="fas fa-trash"></i> 삭제
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>현재 업로드된 파일이 없습니다.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DownloadsPage;